import { useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * Accessible Yönetim Kurulu card with SEO-friendly accordion pattern.
 * - Compact business-card layout: small gold-bordered photo on the left,
 *   name/title/toggle on the right; bio expands full-width below.
 * - Bio content is ALWAYS in the DOM (only visually collapsed via CSS
 *   grid-rows animation) so search engines can crawl it.
 * - aria-expanded + aria-controls implement WAI-ARIA disclosure pattern.
 * - schema.org Person microdata via itemProp attributes.
 */
export default function YonetimKuruluCard({ member }) {
  const [open, setOpen] = useState(false);
  const bioId = `bio-${member.slug}`;

  return (
    <article
      data-testid={`yonetim-karti-${member.slug}`}
      className="border border-navy/10 bg-white"
      itemScope
      itemType="https://schema.org/Person"
    >
      {/* Header row — compact photo + text */}
      <div className="p-6 lg:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6 sm:gap-8">
        {/* Compact photo frame */}
        <div className="relative w-28 h-36 sm:w-32 sm:h-40 shrink-0 overflow-hidden">
          <img
            src={member.photo}
            alt={`${member.name} — ${member.title}`}
            loading="lazy"
            itemProp="image"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 border border-gold pointer-events-none" />
          {/* Subtle architectural corner details */}
          <div className="absolute top-2 left-2 w-3 h-[1px] bg-gold" />
          <div className="absolute top-2 left-2 w-[1px] h-3 bg-gold" />
          <div className="absolute bottom-2 right-2 w-3 h-[1px] bg-gold" />
          <div className="absolute bottom-2 right-2 w-[1px] h-3 bg-gold" />
        </div>

        {/* Text + toggle */}
        <div className="flex-1 min-w-0">
          <div className="overline text-anthracite/60 mb-2">Yönetim Kurulu</div>

          <h3
            data-testid={`yonetim-isim-${member.slug}`}
            itemProp="name"
            className="font-heading text-xl md:text-2xl font-medium text-navy tracking-tight leading-snug"
          >
            {member.name}
          </h3>

          <div
            className="mt-2 text-[12px] tracking-[0.22em] uppercase text-gold font-medium"
            itemProp="jobTitle"
          >
            {member.title}
          </div>

          <div className="mt-4 w-10 h-[1px] bg-gold" />

          {/* Hidden microdata for worksFor */}
          <meta itemProp="worksFor" content="1001 Mimarlık İnşaat ve Ticaret A.Ş." />

          {/* Toggle button */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls={bioId}
            data-testid={`yonetim-toggle-${member.slug}`}
            className="mt-5 inline-flex items-center gap-3 text-navy hover:text-gold transition-colors text-[11.5px] tracking-widest uppercase font-medium"
          >
            {open ? "Özgeçmişi Gizle" : "Özgeçmişi Görüntüle"}
            <ChevronDown
              size={15}
              className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
            />
          </button>
        </div>
      </div>

      {/* Bio content — always in DOM; grid-rows transition for accessibility & SEO */}
      <div
        id={bioId}
        data-testid={`yonetim-bio-${member.slug}`}
        aria-hidden={!open}
        className={`grid transition-[grid-template-rows,opacity] duration-500 ease-out ${
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden min-h-0">
          <div className="px-6 lg:px-8 pb-8 pt-2 border-t border-navy/10">
            <p
              itemProp="description"
              className="text-[14.5px] leading-relaxed text-anthracite/85 font-light"
            >
              {member.bio}
            </p>
          </div>
        </div>
      </div>
    </article>
  );
}
